import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Paper,
  TextField,
} from '@mui/material';
import { colors } from '../utils/colors';
import { trackFormWithAttribution, trackConversionWithAttribution } from '../utils/utmTracking';

function TryContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = React.useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, success: false, error: false, message: '' });

    try {
      let response;

      try {
        const apiUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3001/api/send-email'
          : '/api/send-email';

        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            message: formData.message,
          }),
        });
      } catch (apiError) {
        // API error - falling back to Formspree
        response = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            message: formData.message,
            _subject: 'Try Ulpian Request',
            _replyto: formData.email,
          }),
        });
      }

      if (response.ok) {
        await response.json();
        // Form submitted successfully
        trackFormWithAttribution('contact_form', {
          has_message: !!formData.message,
          company_provided: !!formData.company
        });
        trackConversionWithAttribution('contact_form');
        setSubmitStatus({
          loading: false,
          success: true,
          error: false,
          message: t('try.success_message')
        });
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        const errorData = await response.json();
        // Server returned an error
        throw new Error(errorData.message || 'Form submission failed');
      }
    } catch (error) {
      // Form submission failed
      setSubmitStatus({
        loading: false,
        success: false,
        error: true,
        message: t('try.error_message')
      });
    }
  };

  return (
    <Box
      id="try"
      sx={{
        py: 8,
        backgroundColor: colors.offWhite,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 4,
                color: colors.darkRed,
                fontWeight: 'bold',
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('contact.title')}
            </Typography>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, md: 4 },
                backgroundColor: 'white',
                borderTop: `4px solid ${colors.red}`,
                overflow: 'hidden',
                height: { xs: 'auto', md: 'calc(100% - 80px)' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: { xs: '500px', md: '600px' },
                  minHeight: { xs: '500px', md: '600px' },
                  '& iframe': {
                    border: 'none',
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                <iframe
                  src="https://meetings.hubspot.com/sandra-catovic-strucic/tool-demonstration?embed=true"
                  title="Schedule a Demo"
                  loading="lazy"
                  allowFullScreen
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 4,
                color: colors.darkRed,
                fontWeight: 'bold',
                fontSize: { xs: '2rem', md: '3rem' },
                fontFamily: 'Exo, sans-serif',
              }}
            >
              {t('try.title').split('Ulpian').map((part, index, array) => (
                <React.Fragment key={index}>
                  {part}
                  {index < array.length - 1 && (
                    <span style={{ fontFamily: 'TanAshford, serif' }}>Ulpian</span>
                  )}
                </React.Fragment>
              ))}
            </Typography>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 6 },
                backgroundColor: 'white',
                borderTop: `4px solid ${colors.red}`,
                height: { xs: 'auto', md: 'calc(100% - 80px)' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="body1" sx={{ mb: 4, color: colors.darkRed }}>
                {t('try.subtitle')}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  name="name"
                  label={t('try.name')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3 }}
                />
                <TextField
                  fullWidth
                  name="email"
                  label={t('try.email')}
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3 }}
                />
                <TextField
                  fullWidth
                  name="phone"
                  label={t('try.phone')}
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3 }}
                />
                <TextField
                  fullWidth
                  name="company"
                  label={t('try.company')}
                  value={formData.company}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3 }}
                />
                <TextField
                  fullWidth
                  name="message"
                  label={t('try.message')}
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  sx={{ mb: 4 }}
                />
                <Box sx={{ mt: 'auto' }}>
                  {submitStatus.success ? (
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'green',
                        textAlign: 'center',
                        mb: 2
                      }}
                    >
                      {submitStatus.message}
                    </Typography>
                  ) : submitStatus.error ? (
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.red,
                        textAlign: 'center',
                        mb: 2
                      }}
                    >
                      {submitStatus.message}
                    </Typography>
                  ) : null}
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={submitStatus.loading}
                    sx={{
                      backgroundColor: colors.red,
                      color: colors.yellow,
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      '&:hover': { backgroundColor: colors.darkRed },
                      '&:disabled': {
                        backgroundColor: colors.darkRed,
                        opacity: 0.7,
                      },
                    }}
                  >
                    {submitStatus.loading ? t('try.submitting') : t('try.submit')}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default TryContactSection;
